import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { EventsModuleConfigurationDetailForm } from "../../forms/EventsModuleConfigurationDetailForm";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { CopyText } from "../../components/Utilities/CopyText";
import { PlusIcon } from "../../styles/icons";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

interface EventsModuleConfigurationDetailProps {
  create?: boolean;
}

export const EventsModuleConfigurationDetail: React.FC<EventsModuleConfigurationDetailProps> =
  (props) => {
    const [searchParams] = useSearchParams();
    const { businessId } = useParams();
    const navigate = useNavigate();
    const { showErrorToast, showSuccessToast } = useToast();
    const adminApiClient = useAdminApiClient();

    const [configuration, setConfiguration] =
      useState<CherryHubAdminApi.EventsModuleConfiguration | null>();
    const [loading, setLoading] = useState(!props.create);
    const [error, setError] = useState<string | undefined>(undefined);
    const [editMode, setEditMode] = useState<boolean>(
      props.create ??
        (searchParams.get("edit") !== null &&
        (searchParams.get("edit") === "" || searchParams.get("edit") === "true")
          ? true
          : false)
    );

    useEffect(() => {
      if (props.create) {
        // For create mode, initialize with default values and skip loading
        setConfiguration({
          id: "",
          BusinessId: businessId,
          ScanSettings: {
            MemberFieldLookupOrder: [],
            ValidateUsingRegex: false,
            ScanDataRegex: null,
            ScanDataRegexKeepsMatches: false,
            AllowAlphaCharacters: false,
            AllowDigits: false,
            AllowNonAlphanumericCharacters: false,
          },
        });
        setLoading(false);
        return;
      }

      const fetchConfiguration = async () => {
        if (!businessId) {
          setError("Business ID is required");
          setLoading(false);
          return;
        }

        try {
          const result = await adminApiClient.getEventsModuleConfiguration(
            businessId
          );
          if (result.ok && result.data) {
            setConfiguration(result.data);
          } else {
            // If no configuration exists, set configuration to null
            setConfiguration(null);
          }
        } catch (err) {
          setError("Failed to load Events Module Configuration");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchConfiguration();
    }, [businessId, adminApiClient, props.create]);

    const handleCancel = () => {
      if (props.create) {
        navigate(`/admin/businesses/${businessId}/events-module-configuration`);
      } else {
        setEditMode(false);
      }
    };

    const handleEdit = () => {
      // If no configuration exists yet, initialize with default values when entering edit mode
      if (!configuration) {
        setConfiguration({
          id: "",
          BusinessId: businessId,
          ScanSettings: {
            MemberFieldLookupOrder: [],
            ValidateUsingRegex: false,
            ScanDataRegex: null,
            ScanDataRegexKeepsMatches: false,
            AllowAlphaCharacters: false,
            AllowDigits: false,
            AllowNonAlphanumericCharacters: false,
          },
        });
      }
      setEditMode(true);
    };

    const handleSuccess = useCallback(
      (updatedConfiguration?: CherryHubAdminApi.EventsModuleConfiguration) => {
        setConfiguration(updatedConfiguration);
        if (props.create) {
          showSuccessToast("Configuration updated.");
          navigate(
            `/admin/businesses/${businessId}/events-module-configuration`
          );
        } else {
          showSuccessToast("Configuration updated.");
          setEditMode(false);
        }
      },
      [businessId, navigate, props.create, showSuccessToast]
    );

    const handleFailure = () => {
      showErrorToast("Configuration update failed.");
    };

    if (loading) {
      return <LoadingIndicator />;
    }

    if (error && !editMode && !props.create) {
      return (
        <>
          <PageHeading>
            <PageHeading.Title>Events Module Configuration</PageHeading.Title>
          </PageHeading>
          <PageContent>
            <VStack alignItems="flex-start" width="100%">
              <BusinessBreadcrumbs variant="pageHeading">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={Link}
                    to={`/admin/businesses/${businessId}/events-module-configuration`}
                  >
                    Events Module Configuration
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BusinessBreadcrumbs>

              <VStack w="100%" p={4} spacing={2}>
                <Text fontStyle="italic" fontSize="sm">
                  Events Module Configuration has not been set.
                </Text>
                <Button
                  size="sm"
                  leftIcon={<PlusIcon />}
                  onClick={() => {
                    navigate(
                      `/admin/businesses/${businessId}/events-module-configuration/new`
                    );
                  }}
                  colorScheme="cherryButton"
                >
                  Create Events Module Configuration
                </Button>
              </VStack>
            </VStack>
          </PageContent>
        </>
      );
    }

    if (editMode || props.create) {
      return (
        <>
          <PageHeading>
            <PageHeading.Title>
              {props.create ? "Create" : "Edit"} Events Module Configuration
            </PageHeading.Title>
          </PageHeading>
          <PageContent>
            <VStack alignItems="flex-start" width="100%">
              <BusinessBreadcrumbs variant="pageHeading">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={Link}
                    to={`/admin/businesses/${businessId}/events-module-configuration`}
                  >
                    Events Module Configuration
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BusinessBreadcrumbs>

              {configuration && (
                <EventsModuleConfigurationDetailForm
                  apiClient={adminApiClient}
                  businessId={businessId || ""}
                  configuration={configuration}
                  onCancel={handleCancel}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                />
              )}
            </VStack>
          </PageContent>
        </>
      );
    }

    // View mode - display the configuration details
    return (
      <>
        <PageHeading>
          <PageHeading.Title>Events Module Configuration</PageHeading.Title>
        </PageHeading>
        <PageContent>
          <VStack alignItems="flex-start" width="100%">
            <BusinessBreadcrumbs variant="pageHeading">
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={Link}
                  to={`/admin/businesses/${businessId}/events-module-configuration`}
                >
                  Events Module Configuration
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BusinessBreadcrumbs>
            <>
              <HStack w="100%">
                <Box flexGrow={1} pb={4}>
                  {configuration && configuration.Meta?.Id && (
                    <CopyText text={configuration.Meta.Id} hover adjustMargin>
                      <Text fontSize="md" color="cherryUi.600">
                        {configuration.Meta.Id}
                      </Text>
                    </CopyText>
                  )}
                </Box>
                <Box>
                  <BusinessMenu businessId={businessId!} onClick={handleEdit} />
                </Box>
              </HStack>

              {configuration && (
                <Accordion
                  w="100%"
                  allowToggle
                  allowMultiple
                  variant="section"
                  defaultIndex={[0]}
                >
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Scan Settings</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Member Field Lookup Order"
                              value={
                                configuration.ScanSettings?.MemberFieldLookupOrder?.join(
                                  ", "
                                ) || "-"
                              }
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Validate Using Regex"
                              value={
                                configuration.ScanSettings?.ValidateUsingRegex
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            {configuration.ScanSettings?.ValidateUsingRegex && (
                              <>
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Scan Data Regex"
                                  value={
                                    configuration.ScanSettings?.ScanDataRegex ||
                                    "-"
                                  }
                                />

                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Scan Data Regex Keeps Matches"
                                  value={
                                    configuration.ScanSettings
                                      ?.ScanDataRegexKeepsMatches
                                      ? "Yes"
                                      : "No"
                                  }
                                />
                              </>
                            )}

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Allow Alpha Characters"
                              value={
                                configuration.ScanSettings?.AllowAlphaCharacters
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Allow Digits"
                              value={
                                configuration.ScanSettings?.AllowDigits
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Allow Non-Alphanumeric Characters"
                              value={
                                configuration.ScanSettings
                                  ?.AllowNonAlphanumericCharacters
                                  ? "Yes"
                                  : "No"
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              )}
            </>
            {!configuration && (
              <VStack w="100%" p={4} spacing={2}>
                <Text>
                  No configuration has been created yet. Select ‘Edit' to create
                  one.
                </Text>
              </VStack>
            )}
          </VStack>
        </PageContent>
      </>
    );
  };
